import { defineComponent, PropType } from "@vue/composition-api";
import { getNameMimeType } from "@scrile/tools/dist/lib/FileHelpers";
import { isIOS } from "@/lib/browserUtils";

const HEIC_EXTENSIONS = ["heic", "heif"];

interface ConfigFileType {
  image: string[];
  video: string[];
  audio: string[];
  document: string[];
}

export default function InputFileMixin(configFileTypes: ConfigFileType) {
  type FileTypeKeys = keyof typeof configFileTypes;

  const normalizeAcceptType = (type: string) => (type === "photo" ? "image" : type);
  const isConfigAcceptType = (type: string): type is FileTypeKeys => {
    return Object.keys(configFileTypes).includes(type);
  };
  // iOS Safari decides by these wildcards whether "Photo Library" offers videos, a list of exact video mime types
  // mixed with documents is not always enough there (CO-15539). The selected files are still checked by extension.
  const mediaWildcards: Record<string, string> = { image: "image/*", video: "video/*" };
  let configMimeTypes: Record<string, string[]> = {};

  // iOS converts photos from the library to JPEG itself unless the page explicitly accepts HEIC,
  // and HEIC from iPhone can't be processed by the server converter
  const skipHeic = typeof navigator !== "undefined" && isIOS();

  for (let type in configFileTypes) {
    if (isConfigAcceptType(type)) {
      const extensions = configFileTypes[type].filter(i => !skipHeic || !HEIC_EXTENSIONS.includes(i.toLowerCase()));
      configMimeTypes[type] = extensions.reduce<string[]>((acc, i) => {
        acc.push("." + i);
        acc.push(getNameMimeType(i));
        return acc;
      }, []);
      if (mediaWildcards[type]) configMimeTypes[type].unshift(mediaWildcards[type]);
    }
  }

  return defineComponent({
    name: "InputFileMixin",
    props: {
      accept: { type: [Array, String] as PropType<string | Array<string>> },
    },
    computed: {
      acceptTypes(): string {
        if (!this.accept) return "*/*";
        let accept: string[] = Array.isArray(this.accept) ? this.accept : [this.accept];

        return accept
          .map(t => {
            t = normalizeAcceptType(t);
            if (isConfigAcceptType(t)) return Array.from(new Set(configMimeTypes[t])).join(", ");
            const res = [t];
            if (t.lastIndexOf(".") > 0 && t.indexOf("/") === -1) res.push(getNameMimeType(t));
            return res.join(", ");
          })
          .join(", ");
      },
    },
  });
}
